import React, { useRef, useState, useEffect } from 'react';
import Gallery from './Gallery';
import { galleryData } from '../data/galleryData';
import { 
  fetchLiveScores,
  fetchUpcomingFixtures,
  fetchLeagueStandings,
  fetchSoccerNews,
  getSoccerFacts,
  formatMatchTime,
  getLeagueInfo
} from '../utils/soccerAPI';
import './EnhancedComponents.css';

const FutbolEnhanced = () => {
  const galleryRef = useRef();
  const [showMore, setShowMore] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // State for dynamic content
  const [liveScores, setLiveScores] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [standings, setStandings] = useState([]);
  const [soccerNews, setSoccerNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAllSoccerContent();
  }, []);

  const fetchAllSoccerContent = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [scoresResult, fixturesResult, standingsResult, newsResult] = await Promise.allSettled([
        fetchLiveScores(),
        fetchUpcomingFixtures(),
        fetchLeagueStandings(),
        fetchSoccerNews()
      ]);

      if (scoresResult.status === 'fulfilled') {
        setLiveScores(scoresResult.value || []);
      }
      
      if (fixturesResult.status === 'fulfilled') {
        setFixtures(fixturesResult.value || []);
      }
      
      if (standingsResult.status === 'fulfilled') {
        setStandings(standingsResult.value || []);
      }
      
      if (newsResult.status === 'fulfilled') {
        setSoccerNews(newsResult.value || []);
      }

    } catch (err) {
      console.error('Error fetching soccer content:', err);
      setError('Unable to load some soccer content');
    } finally {
      setLoading(false);
    }
  };

  const handleViewGolazo = () => {
    galleryRef.current?.openGallery(0);
  };

  const handleCelebration = () => {
    setShowMore(!showMore);
  };

  const getMatchStatus = (status) => {
    const statusMap = {
      'LIVE': { text: 'LIVE', class: 'live' },
      'FT': { text: 'Full Time', class: 'finished' },
      'HT': { text: 'Half Time', class: 'halftime' },
      'SCHEDULED': { text: 'Upcoming', class: 'scheduled' },
      'POSTPONED': { text: 'Postponed', class: 'postponed' }
    };
    return statusMap[status] || { text: status, class: 'default' };
  };

  const renderTabContent = () => {
    switch(activeTab) {
      case 'live':
        return (
          <div className="tab-content live-tab">
            <h4>Live Scores & Results</h4>
            {liveScores.length > 0 ? (
              <div className="matches-list">
                {liveScores.map((match, index) => (
                  <div key={index} className="match-card">
                    <div className="match-header">
                      <span className="league">{match.league}</span>
                      <span className={`match-status ${getMatchStatus(match.status).class}`}>
                        {getMatchStatus(match.status).text}
                      </span>
                    </div>
                    <div className="match-teams">
                      <div className="team home">
                        <img src={match.homeTeam.logo} alt={match.homeTeam.name} className="team-logo" />
                        <span className="team-name">{match.homeTeam.name}</span>
                        <span className="team-score">{match.homeTeam.score}</span>
                      </div>
                      <div className="match-time">
                        {match.status === 'LIVE' ? `${match.minute}'` : formatMatchTime(match.utcDate)}
                      </div>
                      <div className="team away">
                        <span className="team-score">{match.awayTeam.score}</span>
                        <span className="team-name">{match.awayTeam.name}</span>
                        <img src={match.awayTeam.logo} alt={match.awayTeam.name} className="team-logo" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p>No live matches available at the moment.</p>
            )}
          </div>
        );

      case 'fixtures':
        return (
          <div className="tab-content fixtures-tab">
            <h4>Upcoming Fixtures</h4>
            {fixtures.length > 0 ? (
              <div className="fixtures-list">
                {fixtures.slice(0, 10).map((fixture, index) => (
                  <div key={index} className="fixture-card">
                    <div className="fixture-date">
                      {new Date(fixture.utcDate).toLocaleDateString('en-US', { 
                        weekday: 'short', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </div>
                    <div className="fixture-time">
                      {new Date(fixture.utcDate).toLocaleTimeString('en-US', { 
                        hour: 'numeric', 
                        minute: '2-digit' 
                      })}
                    </div>
                    <div className="fixture-match">
                      <span className="home-team">{fixture.homeTeam.name}</span>
                      <span className="vs">vs</span>
                      <span className="away-team">{fixture.awayTeam.name}</span>
                    </div>
                    <div className="fixture-league">{fixture.competition.name}</div>
                  </div>
                ))}
              </div>
            ) : (
              <p>No upcoming fixtures available.</p>
            )}
          </div>
        );

      case 'standings':
        return (
          <div className="tab-content standings-tab">
            <h4>League Standings</h4>
            <div className="data-freshness">
              <i className="fas fa-clock"></i>
              <span>Data updates every few hours from live sources</span>
            </div>
            {standings.length > 0 ? (
              <div className="standings-container">
                {standings.map((league, leagueIndex) => (
                  <div key={leagueIndex} className="league-standings">
                    <h5>{league.competition.name}</h5>
                    <div className="standings-table">
                      <div className="standings-header">
                        <span className="pos">#</span>
                        <span className="team">Team</span>
                        <span className="played">P</span>
                        <span className="won">W</span>
                        <span className="drawn">D</span>
                        <span className="lost">L</span>
                        <span className="points">Pts</span>
                      </div>
                      {league.table.map((team, index) => (
                        <div key={index} className="standings-row">
                          <span className="pos">{team.position}</span>
                          <span className="team">
                            <img 
                              src={team.team.crest || "/images/futbol.png"} 
                              alt={team.team.name} 
                              className="team-crest"
                              onError={(e) => {
                                e.target.src = "/images/futbol.png";
                              }}
                            />
                            <span className="team-name">{team.team.name}</span>
                          </span>
                          <span className="played">{team.playedGames}</span>
                          <span className="won">{team.won}</span>
                          <span className="drawn">{team.draw}</span>
                          <span className="lost">{team.lost}</span>
                          <span className="points">{team.points}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p>No standings data available.</p>
            )}
          </div>
        );

      case 'news':
        return (
          <div className="tab-content news-tab">
            <h4>Soccer News</h4>
            {soccerNews.length > 0 ? (
              <div className="soccer-news-list">
                {soccerNews.map((article, index) => (
                  <div key={index} className="news-item">
                    <div className="news-header">
                      <h5>
                        <a href={article.url} target="_blank" rel="noreferrer">
                          {article.title}
                        </a>
                      </h5>
                      <span className="news-category">{article.category}</span>
                    </div>
                    <p className="news-summary">{article.summary}</p>
                    <div className="news-meta">
                      <span className="news-source">{article.source}</span>
                      <span className="news-date">
                        {new Date(article.publishedAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p>No soccer news available.</p>
            )}
          </div>
        );

      case 'facts':
        return (
          <div className="tab-content facts-tab">
            <h4>Soccer Facts & Trivia</h4>
            <div className="facts-list">
              {getSoccerFacts().map((fact, index) => (
                <div key={index} className="fact-item">
                  <div className="fact-category">{fact.category}</div>
                  <div className="fact-text">{fact.fact}</div>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return (
          <div className="tab-content overview-tab">
            {/* Quick Match Summary */}
            {liveScores.length > 0 && (
              <div className="live-summary">
                <h4><i className="fas fa-broadcast-tower"></i> Live Now</h4>
                <div className="live-matches">
                  {liveScores.slice(0, 3).map((match, index) => (
                    <div key={index} className="live-match-summary">
                      <span className="teams">{match.homeTeam.name} vs {match.awayTeam.name}</span>
                      <span className="score">{match.homeTeam.score} - {match.awayTeam.score}</span>
                      <span className="minute">{match.minute}'</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Stats */}
            <div className="soccer-quick-stats">
              <div className="stat-item">
                <strong>World's Sport:</strong> 4+ billion fans
              </div>
              <div className="stat-item">
                <strong>FIFA Members:</strong> 211 countries
              </div>
              <div className="stat-item">
                <strong>World Cup:</strong> Every 4 years
              </div>
              <div className="stat-item">
                <strong>Players:</strong> 270+ million worldwide
              </div>
            </div>

            {/* Popular Leagues */}
            <div className="popular-leagues">
              <h4>Popular Leagues</h4>
              <div className="leagues-grid">
                {getLeagueInfo().map((league, index) => (
                  <div key={index} className="league-card">
                    <img src={league.logo} alt={league.name} className="league-logo" />
                    <span className="league-name">{league.name}</span>
                    <span className="league-country">{league.country}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <section id="futbol" className="section futbol-section enhanced-futbol">
      <div className="section-header">
        <img src="/images/futbol.png" alt="Futbol" className="section-icon" />
        <h1>Futbol, Calcio, Soccer...</h1>
      </div>

      <p>
        The <b>Beautiful Game</b> that unites the world! Football (or soccer) is the most popular sport globally, bringing together billions of fans across continents. From the World Cup to local leagues, futbol represents passion, skill, teamwork, and the pure joy of competition on the pitch.
      </p>

      {/* Dynamic Content Tabs - Always Visible */}
      <div className="content-tabs">
        <div className="tab-buttons">
          <button 
            className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <i className="fas fa-home"></i>
            Overview
          </button>
          <button 
            className={`tab-btn ${activeTab === 'live' ? 'active' : ''}`}
            onClick={() => setActiveTab('live')}
          >
            <i className="fas fa-broadcast-tower"></i>
            Live Scores
          </button>
          <button 
            className={`tab-btn ${activeTab === 'fixtures' ? 'active' : ''}`}
            onClick={() => setActiveTab('fixtures')}
          >
            <i className="fas fa-calendar-alt"></i>
            Fixtures
          </button>
          <button 
            className={`tab-btn ${activeTab === 'standings' ? 'active' : ''}`}
            onClick={() => setActiveTab('standings')}
          >
            <i className="fas fa-list-ol"></i>
            Standings
          </button>
          <button 
            className={`tab-btn ${activeTab === 'news' ? 'active' : ''}`}
            onClick={() => setActiveTab('news')}
          >
            <i className="fas fa-newspaper"></i>
            News
          </button>
          <button 
            className={`tab-btn ${activeTab === 'facts' ? 'active' : ''}`}
            onClick={() => setActiveTab('facts')}
          >
            <i className="fas fa-lightbulb"></i>
            Facts
          </button>
        </div>

        <div className="tab-content-container">
          {loading && (
            <div className="loading-indicator">
              <i className="fas fa-spinner fa-spin"></i>
              Loading soccer data...
            </div>
          )}

          {error && (
            <div className="error-message">
              <i className="fas fa-exclamation-triangle"></i>
              {error}
            </div>
          )}

          {!loading && renderTabContent()}
        </div>
      </div>

      {/* Personal Soccer Story - Hidden by default */}
      {showMore && (
        <div className="extended-intro">
          <div className="soccer-story">
            <h3><i className="fas fa-heart"></i> The Beautiful Game</h3>
            <p>The passion that I have for Futbol, the passion my family has for Futbol. I can't explain it, I can't put it into words, it's a feeling, it's a way of life, it's religion, politics, war & peace.</p>
          </div>
        </div>
      )}

      <div className="cta-buttons">
        <button className="cta-btn primary" onClick={handleCelebration}>
          <i className="fas fa-trophy"></i>
          {showMore ? 'Hide Story' : '90+'}
        </button>

        <button className="cta-btn secondary" onClick={handleViewGolazo}>
          <i className="fas fa-futbol"></i>
          Golazo
        </button>

        <a 
          href="https://www.fifa.com" 
          target="_blank" 
          rel="noreferrer"
          className="cta-btn tertiary"
        >
          <i className="fas fa-globe"></i>
          FIFA.com
        </a>
      </div>
      
      <Gallery ref={galleryRef} images={galleryData.futbol} sectionName="Futbol is Life" maxDisplay={4} />
    </section>
  );
};

export default FutbolEnhanced;